import { create } from 'zustand';
import { ProcessingResultViewModel } from '@/types/document-types';
import { REVIEW_STAGES } from '@/domain/document-processing/constants/processing-stages';
import { Entity } from '@/domain/document-processing/types/entity-types';
import { ExtendedProcessingData } from '@/types/consolidation-types';
import { createLogger } from '@/lib/logging';
import type { HumanIdentityReview } from '@/types/identity-review-types';
import type { VerificationTiming } from '@/types/verification-timing';

const logger = createLogger('DocumentStore');

// Define consolidated entity types
interface ConsolidatedEntity {
  id: string;
  name: string;
  type: string;
  variations: Entity[];
  relatedAddresses: Entity[];
  individuals: Entity[];
  confidence: number;
  sourceDocuments: string[];
  complianceStatus?: 'flagged' | 'approved';
  complianceData?: any;
  nameVariations?: string[];
  addresses?: any[];
  contacts?: any[];
  roles?: string[];
  organization?: string;
  role?: string;
}

export interface ConsolidationResult {
  snapshotId?: string;
  humanIdentityReviews?: HumanIdentityReview[];
  consolidatedEntities: ConsolidatedEntity[];
  rawEntities: Entity[];
  metadata: {
    timestamp: string;
    sourceFiles: string[];
    processingTime: number;
    verificationTiming?: VerificationTiming;
  };
  extendedData?: ExtendedProcessingData;
}

// Define processing stages for better tracking
export enum ProcessingStage {
  IDLE = 'idle',
  OCR = 'ocr',
  COMPLIANCE = 'compliance',
  CONSOLIDATION = 'consolidation',
  COMPLETE = 'complete',
  ERROR = 'error'
}

interface DocumentState {
  // Document Processing State
  results: ProcessingResultViewModel[];
  isProcessing: boolean;
  extractedEntities: Entity[];
  
  // Consolidated Entities State
  consolidationResult: ConsolidationResult | null;
  isConsolidating: boolean;
  lastConsolidationError: number | null; // Timestamp of last consolidation error
  showConsolidatedEntities: boolean; // Whether to show consolidated entities instead of raw entities
  
  // EUC State
  selectedEucFile: string | null;
  eucData: {
    consignee?: {
      name?: string;
      address?: string;
      city?: string;
      country?: string;
      postalCode?: string;
      telephone?: string;
    };
    disposition?: {
      type: 'A' | 'B' | 'C' | 'D' | 'E';
      description: string;
    };
    additionalInfo?: string;
    signature?: {
      name?: string;
      title?: string;
      date?: string;
    };
    purchaser?: {
      name?: string;
      title?: string;
      date?: string;
    };
  } | null;
  
  // Review State
  isReviewing: boolean;
  currentReviewStage: keyof typeof REVIEW_STAGES | null;
  entityReviewProgress: Record<string, {
    status: 'pending' | 'processing' | 'completed' | 'error';
    currentStage: keyof typeof REVIEW_STAGES | null;
  }>;
  
  // Document Metadata
  documentMetadata: {
    fileName?: string;
    fileSize?: number;
    uploadDate?: Date;
    processingErrors?: string[];
    uploadStatus?: 'idle' | 'processing' | 'complete' | 'error';
    totalFiles?: number;
    currentFileIndex?: number;
    processingProgress?: number;
    processingStage?: ProcessingStage;
    entityStatus?: 'PENDING' | 'FLAGGED' | 'APPROVED';
  };
  
  // Actions
  setResults: (results: ProcessingResultViewModel[]) => void;
  setProcessing: (isProcessing: boolean) => void;
  setExtractedEntities: (entities: Entity[]) => void;
  setConsolidationResult: (result: ConsolidationResult | null) => void;
  setIsConsolidating: (isConsolidating: boolean) => void;
  setLastConsolidationError: (timestamp: number | null) => void;
  setShowConsolidatedEntities: (show: boolean) => void;
  setReviewing: (isReviewing: boolean) => void;
  setCurrentReviewStage: (stage: keyof typeof REVIEW_STAGES | null) => void;
  startEntityProcessing: (entityId: string) => void;
  completeEntityProcessing: (entityId: string) => void;
  failEntityProcessing: (entityId: string, error: string) => void;
  updateEntityStage: (entityId: string, stage: keyof typeof REVIEW_STAGES) => void;
  setDocumentMetadata: (metadata: Partial<DocumentState['documentMetadata']>) => void;
  updateUploadStatus: (status: 'idle' | 'processing' | 'complete' | 'error') => void;
  setSelectedEucFile: (fileName: string | null) => void;
  setEucData: (data: DocumentState['eucData']) => void;
  reset: () => void;
}

export const useDocumentStore = create<DocumentState>((set) => ({
  // Initial state
  results: [],
  isProcessing: false,
  extractedEntities: [],
  consolidationResult: null,
  isConsolidating: false,
  lastConsolidationError: null,
  showConsolidatedEntities: false,
  selectedEucFile: null,
  eucData: null,
  isReviewing: false,
  currentReviewStage: null,
  entityReviewProgress: {},
  documentMetadata: {
    uploadStatus: 'idle',
    processingStage: ProcessingStage.IDLE
  },
  
  // Actions
  setResults: (results) => set({ results }),
  setProcessing: (isProcessing) => {
    logger.debug('Setting processing state: ' + isProcessing);
    set({ isProcessing });
  },
  setExtractedEntities: (extractedEntities) => set({ extractedEntities }),
  setConsolidationResult: (consolidationResult) => {
    logger.debug('setConsolidationResult', {
      entityCount: consolidationResult?.consolidatedEntities?.length || 0,
      hasExtendedData: !!consolidationResult?.extendedData
    });
    set({ consolidationResult });
  },
  setIsConsolidating: (isConsolidating) => set({ isConsolidating }),
  setLastConsolidationError: (timestamp) => set({ lastConsolidationError: timestamp }),
  setShowConsolidatedEntities: (showConsolidatedEntities) => set({ showConsolidatedEntities }),
  setReviewing: (isReviewing) => set({ isReviewing }),
  setCurrentReviewStage: (currentReviewStage) => {
    logger.debug('Setting review stage: ' + currentReviewStage);
    set({ currentReviewStage });
  },
  startEntityProcessing: (entityId: string) => set((state) => ({
    entityReviewProgress: {
      ...state.entityReviewProgress,
      [entityId]: { status: 'processing', currentStage: 'LOADING_DATA' }
    }
  })),
  completeEntityProcessing: (entityId: string) => set((state) => ({
    entityReviewProgress: {
      ...state.entityReviewProgress,
      [entityId]: { status: 'completed', currentStage: null }
    }
  })),
  failEntityProcessing: (entityId: string) => set((state) => ({
    entityReviewProgress: {
      ...state.entityReviewProgress,
      [entityId]: { status: 'error', currentStage: null }
    }
  })),
  updateEntityStage: (entityId: string, stage: keyof typeof REVIEW_STAGES) => set((state) => ({
    entityReviewProgress: {
      ...state.entityReviewProgress,
      [entityId]: { 
        ...state.entityReviewProgress[entityId],
        currentStage: stage 
      }
    }
  })),
  setDocumentMetadata: (metadata) => set((state) => ({
    documentMetadata: { ...state.documentMetadata, ...metadata }
  })),
  updateUploadStatus: (status) => set((state) => ({
    documentMetadata: { ...state.documentMetadata, uploadStatus: status }
  })),
  setSelectedEucFile: (fileName) => set({ selectedEucFile: fileName }),
  setEucData: (data) => set({ eucData: data }),
  reset: () => set({ 
    results: [], 
    isProcessing: false, 
    extractedEntities: [],
    consolidationResult: null,
    isConsolidating: false,
    lastConsolidationError: null,
    showConsolidatedEntities: false,
    selectedEucFile: null,
    eucData: null,
    isReviewing: false,
    currentReviewStage: null,
    entityReviewProgress: {},
    documentMetadata: {
      uploadStatus: 'idle',
      processingStage: ProcessingStage.IDLE
    },
  }),
}));

// Log consolidation state changes
useDocumentStore.subscribe(
  (state) => state.consolidationResult,
  (consolidationResult) => {
    logger.debug('consolidationResult changed', {
      entityCount: consolidationResult?.consolidatedEntities?.length || 0,
      hasExtendedData: !!consolidationResult?.extendedData
    });
  }
);
